import clsx from "clsx";
import { useMemo } from "react";

import { TRANSLATE_KEYS } from "@features/navigations/breadcrumbs/model/labels";
import type { TicketType } from "@features/ticket-card/model";

import { BrandIcon } from "@shared/ui/brand-icon";
import { Image } from "@shared/ui/image";
import type { PointCardData } from "@shared/ui/point-card";
import { Body } from "@shared/ui/typography";

import type { CardKey } from "../chat-content";

import s from "./card.module.scss";

export type CardProps = {
  cardData?: PointCardData | TicketType;
  onCardClick: () => void;
  type: CardKey;
  clickable: boolean;
  className?: string;
};

export const Card = (props: CardProps) => {
  const { cardData, onCardClick, clickable, type, className } = props;

  const imageData = useMemo<string>(() => {
    if (!cardData) {
      return "/images/ai-thumb.jpg";
    }

    if ("photos" in cardData && cardData.photos) {
      return [...cardData.photos]?.shift()?.url ?? "/images/ai-thumb.jpg";
    }

    if ("image" in cardData && cardData?.image) {
      return cardData?.image?.url ?? "/images/ai-thumb.jpg";
    }

    if ("image_url" in cardData && cardData?.image_url) {
      return String(cardData.image_url);
    }

    return "/images/ai-thumb.jpg";
  }, [cardData]);

  const title = useMemo(() => {
    if (cardData) {
      return (
        ("shortTitle" in cardData && cardData?.shortTitle) || cardData.title
      );
    }
    return null;
  }, [cardData]);

  if (!cardData) return;

  return (
    <div
      className={clsx(s.root, className, {
        [s.clickable]: clickable,
      })}
      onClick={onCardClick}
    >
      <div className={s.image}>
        {imageData && <Image src={imageData} alt="card image" />}
      </div>
      {title !== null && (
        <div className={s.texts}>
          <div>
            <Body size="uppercase-small" weight="semiBold" className={s.type}>
              {TRANSLATE_KEYS[type]}
            </Body>
            <Body size="uppercase" weight="semiBold">
              {title as string}
            </Body>
          </div>
          {cardData.description && type === "accommodations-program" && (
            <Body size="caption" className={s.description}>
              {cardData.description}
            </Body>
          )}
        </div>
      )}
      {cardData.description && type === "accommodations-program" && (
        <Body size="caption" className={s.mobileDescription}>
          {cardData.description}
        </Body>
      )}
      <div className={s.link}>
        <Body size="uppercase-small" weight="semiBold" className={s.price}>
          подробнее
        </Body>
        <BrandIcon
          name="arrow"
          fillColor="violet"
          strokeColor="white"
          className={s.cardIcon}
        />
      </div>
    </div>
  );
};

Card.displayName = "Card";
